import React, { useEffect, useRef, useState, useCallback } from 'react';

// --- Constants ---
// 필요한 상수만 유지하고 사용되지 않는 것들은 제거

const RomanescoBroccoli = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // 설정값 - 초기값을 낮게 설정하여 첫 로딩 속도 개선
  const [depth, setDepth] = useState(5); // 8에서 5로 낮춤
  const [scale, setScale] = useState(0.4); // 약간 높여서 분기 수 줄임
  const [angle, setAngle] = useState(25);
  const [rotation, setRotation] = useState(-90);
  const [autoRotate, setAutoRotate] = useState(false); // 초기에는 회전 비활성화
  const [zoom, setZoom] = useState(0.9); // 초기 크기 약간 줄임
  const [colorScheme, setColorScheme] = useState('green');
  const [quality, setQuality] = useState('medium'); // 'low', 'medium', 'high'
  const [isLoaded, setIsLoaded] = useState(false); // 초기 로딩 상태 추적
  
  // 회전 애니메이션 관련 상태
  const rotationSpeedRef = useRef(0.2);
  const currentRotationRef = useRef(rotation);
  const lastFrameTimeRef = useRef(0); // 프레임 제한용
  
  // 드래그 관련 상태
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const viewOffsetRef = useRef({ x: 0, y: 0 });
  
  // 로마네스코 브로콜리 색상 가져오기
  const getBroccoliColor = useCallback((depth, maxDepth, angle) => {
    const normalizedDepth = depth / maxDepth;
    const brightness = 60 - normalizedDepth * 20;
    
    switch (colorScheme) {
      case 'rainbow':
        const hue = (angle + 180) % 360;
        return `hsl(${hue}, 70%, ${brightness}%)`;
      
      case 'autumn':
        const autumnHue = 40 - normalizedDepth * 30;
        return `hsl(${autumnHue}, 80%, ${brightness}%)`;
      
      default: // 'green'
        const baseHue = 110;
        const hueVariation = normalizedDepth * 20;
        return `hsl(${baseHue - hueVariation}, 70%, ${brightness}%)`;
    }
  }, [colorScheme]);
  
  // 로마네스코 브로콜리 그리기 함수
  const drawFractal = useCallback((
    ctx, 
    x, 
    y, 
    size, 
    angle, 
    currentDepth, 
    maxDepth, 
    viewOffset,
    currentScale
  ) => {
    // 최대 깊이에 도달하면 중단
    if (currentDepth === 0) return;
    
    // 품질 설정에 따라 깊이 제한 - 품질이 낮을수록 깊이 제한이 강화됨
    let depthLimit = maxDepth;
    if (quality === 'low' && currentDepth < maxDepth - 2) return;
    if (quality === 'medium' && currentDepth < maxDepth - 1 && size < 5) return;
    
    // 너무 작은 요소는 그리지 않음 (최적화)
    if (size < 2) return;
    
    // 뷰포트 밖이면 그리지 않음 (최적화)
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    if (
      x + viewOffset.x < -size * 2 || 
      x + viewOffset.x > canvasWidth + size * 2 || 
      y + viewOffset.y < -size * 2 || 
      y + viewOffset.y > canvasHeight + size * 2
    ) {
      return;
    }
    
    // 브로콜리 꽃잎 그리기
    ctx.beginPath();
    ctx.ellipse(
      x + viewOffset.x, 
      y + viewOffset.y, 
      size, 
      size * 0.9,
      angle * Math.PI / 180, 
      0, 
      Math.PI * 2
    );
    
    // 색상 설정
    const color = getBroccoliColor(maxDepth - currentDepth, maxDepth, angle);
    ctx.fillStyle = color;
    
    // 그림자 효과 - 낮은 품질에서는 그림자 줄이거나 제거
    if (quality !== 'low') {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = quality === 'high' ? size * 0.2 : size * 0.1;
      ctx.shadowOffsetX = quality === 'high' ? size * 0.05 : size * 0.03;
      ctx.shadowOffsetY = quality === 'high' ? size * 0.05 : size * 0.03;
    }
    
    ctx.fill();
    
    // 하이라이트 효과 - 낮은 품질에서는 생략
    if (quality !== 'low' && currentDepth <= maxDepth - 2) {
      ctx.beginPath();
      ctx.ellipse(
        x + viewOffset.x - size * 0.25, 
        y + viewOffset.y - size * 0.2, 
        size * 0.4, 
        size * 0.3,
        angle * Math.PI / 180, 
        0, 
        Math.PI * 2
      );
      ctx.fillStyle = `rgba(255, 255, 255, 0.1)`;
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.fill();
    }
    
    // 다음 깊이의 크기 계산
    const newSize = size * currentScale;
    
    // 골든 앵글에 기반한 나선형 패턴
    const goldenAngle = 137.5;
    
    // 첫 번째 자식 (중앙 위)
    const topX = x + Math.cos(angle * Math.PI / 180) * size * 1.1;
    const topY = y + Math.sin(angle * Math.PI / 180) * size * 1.1;
    drawFractal(
      ctx, 
      topX, 
      topY, 
      newSize, 
      angle, 
      currentDepth - 1, 
      maxDepth, 
      viewOffset,
      currentScale
    );
    
    // 자식 수를 품질에 따라 조정
    const childCount = quality === 'low' ? 
                      Math.min(3, currentDepth) : 
                      quality === 'medium' ? 
                      Math.min(4, currentDepth + 1) : 
                      Math.min(5, currentDepth + 1);
    
    // 나머지 자식들 (나선형 배치)
    for (let i = 1; i <= childCount; i++) {
      const childAngle = angle + goldenAngle * i;
      const distance = size * (0.8 + i * 0.1);
      
      const childX = x + Math.cos(childAngle * Math.PI / 180) * distance;
      const childY = y + Math.sin(childAngle * Math.PI / 180) * distance;
      
      drawFractal(
        ctx, 
        childX, 
        childY, 
        newSize * (1 - i * 0.05),
        childAngle, 
        currentDepth - 1, 
        maxDepth, 
        viewOffset,
        currentScale
      );
    }
  }, [getBroccoliColor, quality]);
  
  // 애니메이션 프레임 업데이트 - 프레임 속도 제한 추가
  const updateCanvas = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 프레임 속도 제한 - 품질에 따라 FPS 조절
    const fps = quality === 'low' ? 15 : quality === 'medium' ? 30 : 60;
    const frameInterval = 1000 / fps;
    
    // 마지막 프레임 이후 충분한 시간이 지났는지 확인
    if (timestamp - lastFrameTimeRef.current < frameInterval) {
      animationRef.current = requestAnimationFrame(updateCanvas);
      return;
    }
    
    // 현재 시간 저장
    lastFrameTimeRef.current = timestamp;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // 캔버스 지우기
    ctx.clearRect(0, 0, width, height);
    
    // 배경 그라데이션
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#104050');
    gradient.addColorStop(1, '#082030');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // 자동 회전 업데이트
    if (autoRotate) {
      currentRotationRef.current += rotationSpeedRef.current;
      setRotation(currentRotationRef.current);
    }
    
    // 파라미터 계산
    const baseSize = Math.min(width, height) / 5 * zoom;
    const startX = width / 2;
    const startY = height / 2;
    
    // 그림자 효과 리셋
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    // 로마네스코 브로콜리 그리기
    drawFractal(
      ctx, 
      startX, 
      startY, 
      baseSize, 
      currentRotationRef.current, 
      depth, 
      depth, 
      viewOffsetRef.current,
      scale
    );
    
    // 로딩 완료 설정
    if (!isLoaded) {
      setIsLoaded(true);
    }
    
    // 다음 프레임 요청
    animationRef.current = requestAnimationFrame(updateCanvas);
  }, [depth, scale, autoRotate, zoom, drawFractal, quality, isLoaded]);
  
  // 마우스 이벤트 핸들러
  const handleMouseDown = useCallback((e) => {
    isDraggingRef.current = true;
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    
    const deltaX = e.clientX - lastMousePosRef.current.x;
    const deltaY = e.clientY - lastMousePosRef.current.y;
    
    viewOffsetRef.current = {
      x: viewOffsetRef.current.x + deltaX,
      y: viewOffsetRef.current.y + deltaY
    };
    
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
  }, []);
  
  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);
  
  const handleMouseWheel = useCallback((e) => {
    e.preventDefault();
    
    // 줌 인/아웃
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prevZoom => Math.max(0.5, Math.min(2.5, prevZoom * zoomFactor)));
  }, []);
  
  // 초기화 및 이벤트 리스너 설정 - 지연 로드 추가
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 캔버스 크기 설정
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth * 0.9;
      canvas.height = window.innerHeight * 0.7;
    };
    
    updateCanvasSize();
    
    // 이벤트 리스너 등록
    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleMouseWheel, { passive: false });
    window.addEventListener('resize', updateCanvasSize);
    
    // 초기 로딩 화면 표시
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#082030';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.fillText('로마네스코 브로콜리 프랙탈 로딩 중...', canvas.width / 2, canvas.height / 2);
    
    // 지연 시작 - 초기 UI 렌더링이 먼저 완료되도록
    const timeoutId = setTimeout(() => {
      currentRotationRef.current = rotation;
      
      // 초기화면에서 낮은 품질로 시작
      const initialQuality = 'low';
      setQuality(initialQuality);
      
      // 애니메이션 시작
      lastFrameTimeRef.current = performance.now();
      animationRef.current = requestAnimationFrame(updateCanvas);
      
      // 잠시 후 중간 품질로 자동 업그레이드
      setTimeout(() => {
        setQuality('medium');
      }, 1000);
      
    }, 300);
    
    // 정리 함수
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      clearTimeout(timeoutId);
      
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('wheel', handleMouseWheel);
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [updateCanvas, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseWheel, rotation]);
  
  // 자동 회전 토글
  const toggleAutoRotate = () => {
    setAutoRotate(prev => !prev);
  };
  
  // 설정 변경 핸들러
  const handleDepthChange = (e) => {
    setDepth(parseInt(e.target.value));
  };
  
  const handleScaleChange = (e) => {
    setScale(parseFloat(e.target.value));
  };
  
  const handleAngleChange = (e) => {
    setAngle(parseInt(e.target.value));
  };
  
  const handleRotationSpeedChange = (e) => {
    rotationSpeedRef.current = parseFloat(e.target.value);
  };
  
  // 뷰 리셋
  const resetView = () => {
    viewOffsetRef.current = { x: 0, y: 0 };
    setZoom(0.9);
    currentRotationRef.current = -90;
    setRotation(-90);
  };
  
  // 품질 설정 변경
  const handleQualityChange = (newQuality) => {
    setQuality(newQuality);
  };
  
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>로마네스코 브로콜리 프랙탈</h2>
      
      <div style={canvasContainerStyle}>
        <canvas
          ref={canvasRef}
          style={canvasStyle}
          title="드래그: 이동, 휠: 줌 인/아웃"
        />
        {!isLoaded && (
          <div style={loadingOverlayStyle}>
            <div style={loadingSpinnerStyle}></div>
            <p>로마네스코 브로콜리 로딩 중...</p>
          </div>
        )}
      </div>
      
      <div style={controlsStyle}>
        <div style={controlGroupStyle}>
          <label style={labelStyle}>렌더링 품질</label>
          <div style={buttonGroupStyle}>
            <button
              onClick={() => handleQualityChange('low')}
              style={{
                ...qualityButtonStyle,
                backgroundColor: quality === 'low' ? '#f1c40f' : '#333'
              }}
            >
              저품질 (빠름)
            </button>
            <button
              onClick={() => handleQualityChange('medium')}
              style={{
                ...qualityButtonStyle,
                backgroundColor: quality === 'medium' ? '#3498db' : '#333'
              }}
            >
              중간품질
            </button>
            <button
              onClick={() => handleQualityChange('high')}
              style={{
                ...qualityButtonStyle,
                backgroundColor: quality === 'high' ? '#2ecc71' : '#333'
              }}
            >
              고품질 (느림)
            </button>
          </div>
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="depth" style={labelStyle}>깊이: {depth}</label>
          <input
            id="depth"
            type="range"
            min="3"
            max="8"
            value={depth}
            onChange={handleDepthChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="scale" style={labelStyle}>축소 비율: {scale.toFixed(2)}</label>
          <input
            id="scale"
            type="range"
            min="0.2"
            max="0.5"
            step="0.01"
            value={scale}
            onChange={handleScaleChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="angle" style={labelStyle}>각도: {angle}°</label>
          <input
            id="angle"
            type="range"
            min="10"
            max="40"
            value={angle}
            onChange={handleAngleChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="rotationSpeed" style={labelStyle}>회전 속도: {rotationSpeedRef.current.toFixed(1)}</label>
          <input
            id="rotationSpeed"
            type="range"
            min="0"
            max="1"
            step="0.1"
            defaultValue={rotationSpeedRef.current}
            onChange={handleRotationSpeedChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label style={labelStyle}>색상 스킴</label>
          <div style={buttonGroupStyle}>
            <button
              onClick={() => setColorScheme('green')}
              style={{
                ...colorButtonStyle,
                backgroundColor: colorScheme === 'green' ? '#4CAF50' : '#333'
              }}
            >
              녹색
            </button>
            <button
              onClick={() => setColorScheme('autumn')}
              style={{
                ...colorButtonStyle,
                backgroundColor: colorScheme === 'autumn' ? '#FF9800' : '#333'
              }}
            >
              가을
            </button>
            <button
              onClick={() => setColorScheme('rainbow')}
              style={{
                ...colorButtonStyle,
                background: colorScheme === 'rainbow'
                  ? 'linear-gradient(90deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff)'
                  : '#333'
              }}
            >
              무지개
            </button>
          </div>
        </div>
        
        <div style={buttonContainerStyle}>
          <button onClick={toggleAutoRotate} style={toggleButtonStyle}>
            자동 회전 {autoRotate ? 'OFF' : 'ON'}
          </button>
          <button onClick={resetView} style={resetButtonStyle}>
            뷰 리셋
          </button>
        </div>
      </div>
      
      <div style={instructionStyle}>
        <p>마우스 드래그: 이동 | 휠: 줌 인/아웃</p>
      </div>
    </div>
  );
};

// --- Styled Components ---
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0a1f2e 0%, #0d2839 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  position: 'relative',
  padding: '0.5rem',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset',
  cursor: 'move'
};

const loadingOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(8, 32, 48, 0.9)',
  borderRadius: '12px',
  color: 'white',
  fontSize: '1.2rem'
};

const loadingSpinnerStyle = {
  width: '40px',
  height: '40px',
  margin: '0 0 20px 0',
  border: '4px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '50%',
  borderTop: '4px solid #0a9396',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  }
};

const controlsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
  marginTop: '1.5rem',
  width: '80%',
  maxWidth: '500px',
  background: 'rgba(5, 20, 30, 0.7)',
  padding: '1.5rem',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
};

const controlGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#8adfff'
};

const sliderStyle = {
  width: '100%',
  height: '8px',
  appearance: 'none',
  background: 'linear-gradient(to right, #06304a, #0a9396)',
  outline: 'none',
  borderRadius: '10px',
  cursor: 'pointer'
};

const buttonGroupStyle = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap'
};

const colorButtonStyle = {
  padding: '0.5rem',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  flex: '1 1 auto',
  minWidth: '70px',
  color: '#fff',
  fontWeight: 'bold',
  transition: 'background 0.2s'
};

const qualityButtonStyle = {
  padding: '0.5rem',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  flex: '1 1 auto',
  minWidth: '70px',
  color: '#fff',
  fontWeight: 'bold',
  transition: 'background 0.2s'
};

const buttonContainerStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '0.5rem'
};

const toggleButtonStyle = {
  flex: 1,
  padding: '0.75rem',
  borderRadius: '6px',
  border: 'none',
  background: 'linear-gradient(135deg, #0a9396 0%, #005f73 100%)',
  color: 'white',
  fontWeight: 'bold',
  cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
};

const resetButtonStyle = {
  flex: 1,
  padding: '0.75rem',
  borderRadius: '6px',
  border: 'none',
  background: 'linear-gradient(135deg, #555 0%, #333 100%)',
  color: 'white',
  fontWeight: 'bold',
  cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
};

const instructionStyle = {
  marginTop: '1rem',
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.6)',
  fontStyle: 'italic'
};

export default RomanescoBroccoli; 